import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Loader2, Search, CheckCircle2, X, ExternalLink, UserRound, History, ChevronDown, ChevronUp, UserPlus, Building2, Trash2, FileText, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AddProspectAccountInline } from './AddProspectAccountInline';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level session cache for account searches — persists across modal opens
// within the same tab session to avoid repeating identical Zoho API searches.
const _accountSearchCache = new Map();

const DEFAULT_CALL_PURPOSE = 'Cold Call';  // Outgoing is default direction
const BUSINESS_UNITS = ['Structural', 'Merchant', 'Industrial'];

/**
 * Quick "Add Call to CRM" modal.
 *
 * Two operating modes:
 *   1. Fresh call (default) — rep searches an account, logs a new completed call.
 *   2. Complete-scheduled mode — when `scheduledCall` is provided, the modal
 *      skips search and pre-populates every field from the existing Zoho
 *      scheduled Call record. On submit it PUTs `/zoho/complete-scheduled-call`
 *      to turn that record from Scheduled → Completed instead of creating a
 *      brand new one. `onCompleted(callId)` is fired so the notification bar
 *      can refetch & remove the entry.
 */
export const AddCallToCrmModal = ({ isOpen, onClose, scheduledCall = null, editCall = null, onCompleted = null, onDeleted = null, onSaved = null, onChainToQuote = null, prefillAccount = null, resumeSnapshot = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  const pendingResumeContactIdsRef = useRef(null);
  // Pre-fill datetime-local with "now" in the browser's local timezone
  const _nowLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [callStartTime, setCallStartTime] = useState(_nowLocal());
  const [accountQuery, setAccountQuery] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const [purposes, setPurposes] = useState([]);
  const [purposesLoading, setPurposesLoading] = useState(false);
  const [purpose, setPurpose] = useState('');

  const [description, setDescription] = useState('');
  const [contactIds, setContactIds] = useState([]);  // Multi-select checkboxes
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submittingMode, setSubmittingMode] = useState(null);  // 'close' | 'view'

  // Direction + business unit (C30-local metadata, not stored in Zoho)
  const [callDirection, setCallDirection] = useState('outgoing');  // 'outgoing' | 'incoming'
  const [businessUnit, setBusinessUnit] = useState('');
  const [purposeManuallySet, setPurposeManuallySet] = useState(false);

  // Linked project package — optional, links the call note into the package notes too
  const [linkedPackages, setLinkedPackages] = useState([]);
  const [linkedPackageId, setLinkedPackageId] = useState('__none__');
  const [linkedPackagesLoading, setLinkedPackagesLoading] = useState(false);
  // Success state for the "Add & View" flow — holds the Zoho URL so we can
  // render a plain <a> the user taps. This is the only reliable way on iOS/iPadOS
  // Safari, which blocks any post-await window.open/location.replace.
  const [viewResult, setViewResult] = useState(null);  // { call_url, subject }

  // Previous call notes for the selected account — lazy-loaded when the rep
  // expands the collapsible "Previous call notes" section.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCalls, setHistoryCalls] = useState([]);
  const [historyFetchedFor, setHistoryFetchedFor] = useState(null);  // account.id the cache belongs to

  // Inline "add new prospect account" mini-form — triggered from the account search area.
  const [showAddAccount, setShowAddAccount] = useState(false);

  // Inline "add new contact to this account" mini-form. Expanded via a button
  // under the contact checkbox list. Creates a Zoho Contact linked to the
  // selected Account, then auto-selects it.
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContactFirst, setNewContactFirst] = useState('');
  const [newContactLast, setNewContactLast] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactTitle, setNewContactTitle] = useState('');
  const [addingContact, setAddingContact] = useState(false);

  // Default call duration — hidden from UI. Every call logs as 5 minutes.
  const DEFAULT_DURATION_MINUTES = 5;

  // Complete-scheduled mode: when `scheduledCall` is given, we treat this
  // modal as "finish off a pre-booked call" rather than "create a new call".
  const isCompletingScheduled = !!scheduledCall?.id;
  // Edit-existing mode: any call (scheduled, completed, overdue) opened from
  // the Manage Calls page. Unlike complete-scheduled, this PATCHes the same
  // record in place rather than creating a new Completed one.
  const isEditing = !!editCall?.id;

  const resetAll = () => {
    setCallStartTime(_nowLocal());
    setAccountQuery('');
    setAccounts([]);
    setSelectedAccount(null);
    setContacts([]);
    setContactIds([]);
    setPurpose(DEFAULT_CALL_PURPOSE);
    setPurposeManuallySet(false);
    setCallDirection('outgoing');
    setBusinessUnit('');
    setDescription('');
    setHistoryOpen(false);
    setHistoryCalls([]);
    setHistoryFetchedFor(null);
    setViewResult(null);
    setShowAddContact(false);
    setShowAddAccount(false);
    setNewContactFirst('');
    setNewContactLast('');
    setNewContactPhone('');
    setNewContactEmail('');
    setNewContactTitle('');
    setLinkedPackages([]);
    setLinkedPackageId('__none__');
  };

  // Load purposes once when modal opens
  useEffect(() => {
    if (!isOpen) return;
    resetAll();
    // Prefill from a scheduled-call record, if we're completing one.
    const sourceCall = editCall || scheduledCall;
    if (sourceCall?.id) {
      if (sourceCall.account?.id) {
        setSelectedAccount({
          id: sourceCall.account.id,
          name: sourceCall.account.name || '',
        });
        setAccountQuery(sourceCall.account.name || '');
      }
      if (sourceCall.call_purpose) {
        setPurpose(sourceCall.call_purpose);
        // Mark as manually set so the direction-change effect doesn't override it
        setPurposeManuallySet(true);
      }
      if (sourceCall.description) {
        setDescription(sourceCall.description);
      }
      if (editCall?.call_start_time) {
        try {
          // Convert ISO → `YYYY-MM-DDTHH:MM` for the datetime-local input
          const d = new Date(editCall.call_start_time);
          const pad = n => String(n).padStart(2, '0');
          setCallStartTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
        } catch (_) { /* fallthrough to default */ }
      }
      // Pre-fill call type/direction from editCall (Zoho stores "Inbound"/"Outbound")
      if (editCall?.call_type) {
        setCallDirection(editCall.call_type.toLowerCase() === 'inbound' ? 'incoming' : 'outgoing');
      }
      // Pre-fill business unit from editCall (stored in C30 metadata, enriched on fetch)
      if (editCall?.business_unit) {
        setBusinessUnit(editCall.business_unit);
      }
    }
    // Chain pre-fill — when the user clicked "Create & Then Call" on the
    // quote modal (or opened "View Call" from an 8x8 row), the parent passes
    // the selected account/contact — and, for the 8x8 flow, whatever the rep
    // had already picked/typed inline (purpose, business unit, direction,
    // notes) — through here so nothing has to be re-entered.
    if (!sourceCall && prefillAccount?.account?.id) {
      setSelectedAccount({
        id: prefillAccount.account.id,
        name: prefillAccount.account.name || '',
      });
      setAccountQuery(prefillAccount.account.name || '');
      if (prefillAccount.contact?.id) setContactIds([prefillAccount.contact.id]);
      if (prefillAccount.call_purpose) {
        setPurpose(prefillAccount.call_purpose);
        setPurposeManuallySet(true);
      }
      if (prefillAccount.business_unit) setBusinessUnit(prefillAccount.business_unit);
      if (prefillAccount.direction) setCallDirection(prefillAccount.direction);
      if (prefillAccount.description) setDescription(prefillAccount.description);
    }
    // Modal UI v2 — resume from a minimized "Open Activities" tab. Only
    // applies to fresh calls (no editCall/scheduledCall — those already
    // prefill above from real Zoho data).
    if (!sourceCall && !prefillAccount?.account?.id && resumeSnapshot) {
      if (resumeSnapshot.selectedAccount) {
        setSelectedAccount(resumeSnapshot.selectedAccount);
        setAccountQuery(resumeSnapshot.selectedAccount.name || '');
      }
      if (resumeSnapshot.contactIds) pendingResumeContactIdsRef.current = resumeSnapshot.contactIds;
      if (resumeSnapshot.purpose) { setPurpose(resumeSnapshot.purpose); setPurposeManuallySet(true); }
      if (resumeSnapshot.businessUnit) setBusinessUnit(resumeSnapshot.businessUnit);
      if (resumeSnapshot.callDirection) setCallDirection(resumeSnapshot.callDirection);
      if (resumeSnapshot.description) setDescription(resumeSnapshot.description);
      if (resumeSnapshot.linkedPackageId) setLinkedPackageId(resumeSnapshot.linkedPackageId);
      if (resumeSnapshot.callStartTime) setCallStartTime(resumeSnapshot.callStartTime);
    }
    // (the call purposes are loaded from the CRM for the call's direction by the effect further down)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The purposes offered come straight from the CRM and depend on the direction of the call: outgoing calls use the CRM's Call Purpose
  // list (it includes e.g. "Project Discussion"), incoming calls its shorter Incoming Call Purpose list. A purpose that is not on the
  // list for the chosen direction is cleared so the rep picks a valid one — it is never sent to the CRM unchecked.
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    setPurposesLoading(true);
    api.get(`${API}/zoho/call-purposes`, { params: { direction: callDirection } })
      .then((res) => {
        if (!alive) return;
        const list = res.data?.purposes || [];
        setPurposes(list);
        setPurpose((current) => {
          if (current && list.includes(current)) return current;
          const fallback = callDirection === 'incoming' ? 'Price Enquiry' : DEFAULT_CALL_PURPOSE;
          return list.includes(fallback) ? fallback : '';
        });
      })
      .catch(() => { if (alive) toast.error("Couldn't load the call purposes from the CRM — please try again"); })
      .finally(() => { if (alive) setPurposesLoading(false); });
    return () => { alive = false; };
  }, [isOpen, callDirection]);

  // Debounced account search
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => runAccountSearch(accountQuery), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountQuery, isOpen]);

  // When an account is picked → auto-load its contacts
  useEffect(() => {
    if (!selectedAccount?.id) return;
    setContacts([]);
    setContactIds([]);
    // Invalidate previous-calls cache when switching accounts
    setHistoryCalls([]);
    setHistoryFetchedFor(null);
    setHistoryOpen(false);
    (async () => {
      setContactsLoading(true);
      try {
        const res = await api.get(`${API}/zoho/account/${selectedAccount.id}/contacts`);
        const list = res.data.contacts || [];
        setContacts(list);

        // Auto-select a contact when we're completing a scheduled call:
        //   1. Pre-selected (scheduled against) contact wins if present
        //   2. Otherwise fall back to the first contact
        // Also copy that contact's phone (or the account phone) to the
        // clipboard so the rep can paste straight into their dialer.
        if (isCompletingScheduled && list.length > 0) {
          const preferId = scheduledCall?.contact?.id;
          const match = preferId ? list.find(c => c.id === preferId) : null;
          const pick = match || list[0];
          if (pick?.id) {
            setContactIds([pick.id]);
            const phoneToCopy =
              pick.mobile ||
              pick.phone ||
              scheduledCall?.contact?.phone ||
              scheduledCall?.account?.phone ||
              selectedAccount.phone ||
              null;
            if (phoneToCopy && navigator?.clipboard?.writeText) {
              // Fire-and-forget — don't toast on failure, clipboard perms can be
              // revoked on iPad and the rep can still read the number off screen.
              navigator.clipboard.writeText(String(phoneToCopy)).then(
                () => toast.success(`Copied ${phoneToCopy}`),
                () => { /* silent */ },
              );
            }
          }
        }
        // Pre-select the saved contact when editing a completed call
        if (isEditing && list.length > 0 && editCall?.contact?.id) {
          const match = list.find(c => c.id === editCall.contact.id);
          if (match) setContactIds([match.id]);
        }
        // For fresh call creation, auto-select the first contact so the rep
        // doesn't have to pick — they can always change it.
        if (!isCompletingScheduled && !isEditing && !prefillAccount?.contact?.id && list.length > 0) {
          setContactIds([list[0].id]);
        }
      } catch (_) {
        setContacts([]);
      } finally {
        setContactsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // Re-apply a resumed contact selection once the (re-fetched) contact list
  // for the resumed account has loaded — runs after the effect above so it
  // wins over that effect's own auto-select-first-contact default.
  useEffect(() => {
    if (!contacts.length || !pendingResumeContactIdsRef.current) return;
    setContactIds(pendingResumeContactIdsRef.current);
    pendingResumeContactIdsRef.current = null;
  }, [contacts]);

  // Register/refresh the live Activity Bar entry once an account is picked
  // for a fresh call (not while editing/completing an existing Zoho record).
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !selectedAccount?.id || isEditing || isCompletingScheduled) return;
    if (!activityIdRef.current) activityIdRef.current = `add-call-${selectedAccount.id}-${Date.now()}`;
    const title = `Call — ${selectedAccount.name}`;
    const snapshot = {
      activityId: activityIdRef.current, selectedAccount, contactIds, purpose, businessUnit,
      callDirection, description, linkedPackageId, callStartTime,
    };
    activityBar.openActivity({ type: 'add_call_to_crm', activityId: activityIdRef.current, title, subtitle: 'Add Call to CRM', snapshot });
    activityBar.logRecentView({ type: 'add_call_to_crm', activityId: activityIdRef.current, title, subtitle: 'Add Call to CRM', snapshot });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, selectedAccount?.id]);

  // Keep the live snapshot fresh as the rep fills in the form.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !activityIdRef.current) return;
    activityBar.updateActiveSnapshot({
      activityId: activityIdRef.current, selectedAccount, contactIds, purpose, businessUnit,
      callDirection, description, linkedPackageId, callStartTime,
    }, { title: `Call — ${selectedAccount?.name || ''}`, subtitle: 'Add Call to CRM' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, selectedAccount, contactIds, purpose, businessUnit, callDirection, description, linkedPackageId, callStartTime]);

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);

  // When an account is picked → fetch linked project packages
  useEffect(() => {
    if (!selectedAccount?.id) {
      setLinkedPackages([]);
      setLinkedPackageId('__none__');
      return;
    }
    setLinkedPackagesLoading(true);
    api.get(`${API}/zoho/account/${selectedAccount.id}/linked-packages`)
      .then(res => setLinkedPackages(res.data.packages || []))
      .catch(() => setLinkedPackages([]))
      .finally(() => setLinkedPackagesLoading(false));
  }, [selectedAccount?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-fetch previous-calls the first time the rep expands the history panel
  // (or revisits it after selecting a different account).
  const toggleHistory = async () => {
    const willOpen = !historyOpen;
    setHistoryOpen(willOpen);
    if (!willOpen) return;
    if (!selectedAccount?.id) return;
    if (historyFetchedFor === selectedAccount.id) return;  // already cached
    setHistoryLoading(true);
    try {
      const res = await api.get(`${API}/zoho/account/${selectedAccount.id}/previous-calls`, { params: { limit: 10 } });
      setHistoryCalls(res.data?.calls || []);
      setHistoryFetchedFor(selectedAccount.id);
    } catch (_) {
      setHistoryCalls([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Format a Zoho ISO datetime like "2026-04-22T10:15:00+00:00" into a readable
  // local string e.g. "22 Apr 2026, 10:15". Degrades gracefully on parse errors.
  const _formatZohoDate = (s) => {
    if (!s) return '';
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (_) { return s; }
  };

  const runAccountSearch = async (q) => {
    if (!q || q.trim().length < 2) {
      setAccounts([]);
      return;
    }
    const cacheKey = q.trim().toLowerCase();
    if (_accountSearchCache.has(cacheKey)) {
      setAccounts(_accountSearchCache.get(cacheKey));
      return;
    }
    setAccountLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q } });
      const results = res.data.accounts || [];
      _accountSearchCache.set(cacheKey, results);
      setAccounts(results);
    } catch (_) {
      setAccounts([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const handleSubmit = async (mode = 'close') => {
    if (!selectedAccount?.id) return toast.error('Pick a customer account first');
    if (!purpose) return toast.error('Pick a call purpose');
    if (!businessUnit) return toast.error('Pick a business unit');
    setSubmitting(true);
    setSubmittingMode(mode);    try {
      // Primary contact = first checked. Additional contacts' names get appended to
      // the description since Zoho's Calls module has a single Who_Id per record.
      const primaryId = contactIds[0] || null;
      const extras = contactIds.slice(1)
        .map(id => contacts.find(c => c.id === id)?.full_name)
        .filter(Boolean);
      const finalDescription = [
        description || '',
        extras.length ? `Also spoke with: ${extras.join(', ')}` : '',
      ].filter(Boolean).join('\n\n').trim();

      let res;
      if (isEditing) {
        // PATCH the existing record in place — no new call created.
        const payload = {
          new_start_time: callStartTime || null,
          new_purpose: purpose,
          new_subject: `${purpose} — ${selectedAccount.name}`,
          new_description: finalDescription || '',
        };
        if (primaryId) payload.new_contact_id = primaryId;
        res = await api.patch(`${API}/zoho/scheduled-call/${editCall.id}`, payload);
        res.data = {
          ...(res.data || {}),
          call_id: editCall.id,
          // Reconstruct the Zoho deep-link for "Save & view in CRM"
          call_url: editCall.call_url || `https://crm.zoho.eu/crm/${(process.env.REACT_APP_ZOHO_CRM_ORG || 'encon')}/tab/Calls/${editCall.id}`,
          subject: payload.new_subject,
        };
      } else if (isCompletingScheduled) {
        // Update the existing Zoho Scheduled record → Completed.
        res = await api.post(`${API}/zoho/complete-scheduled-call`, {
          call_id: scheduledCall.id,
          call_start_time: callStartTime || null,
          call_duration_minutes: DEFAULT_DURATION_MINUTES,
          call_purpose: purpose,
          description: finalDescription || null,
          contact_id: primaryId,
        });
      } else {
        res = await api.post(`${API}/zoho/create-call`, {
          account_id: selectedAccount.id,
          contact_id: primaryId,
          call_start_time: callStartTime || null,
          call_duration_minutes: DEFAULT_DURATION_MINUTES,
          call_type: callDirection === 'incoming' ? 'Inbound' : 'Outbound',
          call_purpose: purpose,
          subject: `${purpose} — ${selectedAccount.name}`,
          description: finalDescription || null,
        });
        if (res.data?.scheduled_merge_failed) {
          toast.warning("Your call is logged, but Zoho wouldn't complete the call that was booked for this customer today — that booking is still open.");
        }
        // Save C30 metadata (business_unit + direction + description backup) — fire-and-forget
        const callId = res.data?.call_id;
        if (callId) {
          api.post(`${API}/zoho/call-metadata`, {
            zoho_call_id: callId,
            business_unit: businessUnit || null,
            direction: callDirection,
            description: finalDescription || null,
          }).catch(() => {});
        }
      }
      toast.success(
        isEditing ? 'Call updated'
        : isCompletingScheduled ? 'Scheduled call marked as completed'
        : 'Call logged in Zoho CRM'
      );
      window.dispatchEvent(new Event('c30:calls-changed'));

      // If a project package is linked, also add the call note into the package notes
      if (linkedPackageId && linkedPackageId !== '__none__') {
        const [pkgModule, pkgId] = linkedPackageId.split(':');
        const contactName = contactIds[0]
          ? contacts.find(c => c.id === contactIds[0])?.full_name
          : null;
        const today = new Date().toLocaleDateString('en-GB');
        const noteTitle = contactName
          ? `Call Note — ${contactName} — ${today}`
          : `Call Note — ${today}`;
        const noteContent = finalDescription || `Call logged on ${today} for ${selectedAccount?.name || 'account'}`;
        api.post(`${API}/zoho/package/${pkgModule}/${pkgId}/notes`, {
          note_title: noteTitle,
          note_content: noteContent,
        }).catch(() => {}); // non-fatal
      }
      if (isEditing) onSaved?.(editCall.id);
      else onCompleted?.(scheduledCall?.id || res.data?.call_id);
      if (mode === 'view' && res.data?.call_url) {
        // Don't try to open the tab ourselves here — iOS/iPadOS Safari blocks
        // any window.open/location navigation after an await (the user-gesture
        // has expired). Instead, render a success card with a real <a> link
        // the user taps: that counts as a fresh user gesture and is always
        // allowed across Chrome/Firefox/Safari on every platform.
        setViewResult({
          call_url: res.data.call_url,
          subject: res.data.subject,
        });
      } else if (mode === 'chain-quote') {
        // Chain → auto-close and ask the parent to open the Add-Quote modal
        // pre-filled with the same account/contact. Rep saves a menu click.
        const primaryContact = contactIds[0]
          ? contacts.find(c => c.id === contactIds[0])
          : null;
        resetAll();
        if (modalUiV2) activityBar.discardActive();
        onClose?.();
        onChainToQuote?.({
          account: selectedAccount,
          contact: primaryContact ? { id: primaryContact.id, name: primaryContact.full_name } : null,
        });
      } else {
        // 'close' mode: dismiss the modal immediately.
        resetAll();
        if (modalUiV2) activityBar.discardActive();
        onClose?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log call');
    } finally {
      setSubmitting(false);
      setSubmittingMode(null);
    }
  };

  const handleDeleteScheduled = async () => {
    const target = editCall || scheduledCall;
    if (!target?.id) return;
    const who = target.account?.name || 'this call';
    if (!window.confirm(`Delete the call for ${who}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`${API}/zoho/scheduled-call/${target.id}`);
      toast.success('Call deleted');
      window.dispatchEvent(new Event('c30:calls-changed'));
      onDeleted?.(target.id);
      resetAll();
      if (modalUiV2) activityBar.discardActive();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddContact = async () => {
    if (!selectedAccount?.id) return;
    const last = (newContactLast || '').trim();
    if (!last) return toast.error('Last name is required');
    setAddingContact(true);
    try {
      const res = await api.post(`${API}/zoho/create-contact`, {
        account_id: selectedAccount.id,
        first_name: newContactFirst.trim() || null,
        last_name: last,
        phone: newContactPhone.trim() || null,
        email: newContactEmail.trim() || null,
        title: newContactTitle.trim() || null,
      });
      const c = res.data?.contact;
      if (c?.id) {
        // Append to the list and auto-select
        setContacts(prev => [...prev, {
          id: c.id,
          full_name: c.full_name || `${newContactFirst} ${last}`.trim(),
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
          phone: c.phone,
          title: c.title,
        }]);
        setContactIds(prev => [...prev, c.id]);
      }
      setShowAddContact(false);
      setNewContactFirst('');
      setNewContactLast('');
      setNewContactPhone('');
      setNewContactEmail('');
      setNewContactTitle('');
      toast.success('Contact added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  if (!isOpen) return null;

  // Shared input styling — bolder borders + white background so inputs read as
  // clearly editable fields, not decorative chrome.
  const fieldCardClass = "rounded-xl border border-gray-200 bg-white shadow-sm p-3 transition-shadow hover:shadow-md";
  const inputClass = "rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10";

  return createPortal(
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-3 ${modalV2OverlayClass(modalUiV2)}`}
      style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }}
    >
      <div className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl w-full max-w-5xl overflow-y-auto border-2 border-orange-300 flex flex-col" style={{ maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '95vh' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 rounded-t-3xl flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
              <Phone className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight leading-tight truncate">{isEditing ? 'Edit Call' : isCompletingScheduled ? 'Complete Scheduled Call' : 'Add Call to CRM'}</h2>
              <p className="text-[11px] text-gray-500 truncate">{isEditing ? 'Update this call in Zoho CRM' : isCompletingScheduled ? 'Finish this booked call and log the outcome' : "Quick log — it'll file straight into Zoho"}</p>
            </div>
          </div>
          {/* Inline date/time picker in header — saves vertical space so the form
              fits without scrolling on iPad. Hidden in the "view success" state. */}
          {!viewResult && (
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              <Input
                type="datetime-local"
                value={callStartTime}
                onChange={(e) => setCallStartTime(e.target.value)}
                className="rounded-2xl border-2 border-orange-200 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10 text-sm w-[240px] px-4"
                data-testid="call-datetime-input"
                aria-label="Call date & time"
              />
            </div>
          )}
          <button onClick={() => { if (modalUiV2) activityBar.discardActive(); onClose?.(); }} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0" data-testid="call-modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Mobile-only date field (sm: hides when header variant is shown) */}
          {!viewResult && (
            <div className="sm:hidden">
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Call Date & Time</Label>
              <Input
                type="datetime-local"
                value={callStartTime}
                onChange={(e) => setCallStartTime(e.target.value)}
                className={`mt-1 ${inputClass}`}
                data-testid="call-datetime-input-mobile"
              />
            </div>
          )}
          {viewResult ? (
            /* Success state for the "Add & View on CRM" flow. A real user-tapped
               <a target="_blank"> link is the only reliable way on iOS/iPadOS
               Safari, which blocks all programmatic window navigation after
               an await. Desktop browsers also handle this cleanly. */
            <div className="space-y-4" data-testid="call-view-success">
              <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5">
                <div className="flex items-center gap-2 text-emerald-700 font-bold mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Call logged in Zoho CRM</span>
                </div>
                {viewResult.subject && (
                  <div className="text-xs text-gray-700">
                    <span className="font-semibold">Subject:</span> {viewResult.subject}
                  </div>
                )}
                <p className="text-xs text-gray-600 mt-2">
                  Tap below to open the call record in Zoho. This opens in a new tab.
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => { resetAll(); if (modalUiV2) activityBar.discardActive(); onClose?.(); }}
                  className="rounded-xl h-11 px-5"
                  data-testid="call-view-done-btn"
                >
                  Done
                </Button>
                <a
                  href={viewResult.call_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { /* fire-and-forget; modal stays open so the link is always tappable again */ }}
                  className="inline-flex items-center justify-center rounded-xl h-11 px-6 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                  style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                  data-testid="call-view-open-link"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open in Zoho
                </a>
              </div>
            </div>
          ) : (
            <>
              {/* ── Row 1: Customer + Contact side by side ─────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                {/* Customer search */}
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Customer</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      value={accountQuery}
                      onChange={(e) => {
                        setAccountQuery(e.target.value);
                        if (selectedAccount) setSelectedAccount(null);
                      }}
                      placeholder="Type any part of the customer name (e.g. huw tel)"
                      className={`flex-1 ${inputClass}`}
                      data-testid="call-account-search"
                    />
                    <Button type="button" onClick={() => runAccountSearch(accountQuery)} disabled={accountLoading} className="rounded-xl h-11 w-11 p-0" style={{ backgroundColor: '#ee7203' }}>
                      {accountLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Search className="w-4 h-4 text-white" />}
                    </Button>
                  </div>
                  {!selectedAccount && (accountLoading || accounts.length > 0) && (
                    <div className="border border-gray-200 rounded-xl divide-y max-h-48 overflow-y-auto mt-2 bg-white shadow-inner">
                      {accountLoading ? (
                        <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                      ) : accounts.length === 0 ? (
                        <div className="p-3 text-center text-gray-500 text-sm">No accounts found</div>
                      ) : (
                        accounts.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => { setSelectedAccount(a); setAccountQuery(a.name); }}
                            className="w-full text-left p-3 hover:bg-orange-50 transition-colors"
                            data-testid={`call-account-option-${a.id}`}
                          >
                            <div className="font-semibold text-sm text-gray-900">{a.name}</div>
                            <div className="text-xs text-gray-500">
                              {[a.city, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  {selectedAccount && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span className="font-semibold flex-1 truncate">{selectedAccount.name}</span>
                      {selectedAccount.phone && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedAccount.phone).catch(() => {});
                            toast.success(`Copied: ${selectedAccount.phone}`);
                          }}
                          className="flex items-center gap-1 text-[11px] font-mono text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded px-1.5 py-0.5 transition-colors flex-shrink-0"
                          title="Click to copy phone number"
                          data-testid="call-account-phone-copy"
                        >
                          <Phone className="w-3 h-3" /> {selectedAccount.phone}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setSelectedAccount(null); setAccountQuery(''); setContacts([]); setContactIds([]); }}
                        className="text-[11px] text-gray-400 hover:text-gray-600 underline flex-shrink-0"
                      >
                        change
                      </button>
                    </div>
                  )}
                  {!selectedAccount && !isCompletingScheduled && !showAddAccount && (
                    <button
                      type="button"
                      onClick={() => setShowAddAccount(true)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
                      data-testid="call-add-account-toggle"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      Can't find them? Add as a new prospect
                    </button>
                  )}
                  {showAddAccount && (
                    <AddProspectAccountInline
                      themeColor="orange"
                      onCancel={() => setShowAddAccount(false)}
                      onCreated={({ account, contact }) => {
                        if (account?.id) {
                          setSelectedAccount({ id: account.id, name: account.name });
                          setAccountQuery(account.name);
                        }
                        if (contact?.id) {
                          setContacts([{
                            id: contact.id,
                            full_name: contact.full_name,
                            first_name: contact.first_name,
                            last_name: contact.last_name,
                            email: contact.email,
                            phone: contact.phone,
                            mobile: contact.mobile,
                          }]);
                          setContactIds([contact.id]);
                        }
                        setShowAddAccount(false);
                      }}
                    />
                  )}
                </div>

                {/* Contact dropdown */}
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1">
                    <UserRound className="w-3.5 h-3.5" /> Contact spoken to
                  </Label>

                  {!selectedAccount ? (
                    <p className="text-xs text-gray-400 mt-2 italic">Select a customer first</p>
                  ) : contactsLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading contacts…
                    </div>
                  ) : contacts.length === 0 ? (
                    <p className="text-xs text-gray-500 mt-2">No contacts on this account in Zoho.</p>
                  ) : (
                    <Select
                      value={contactIds[0] || ''}
                      onValueChange={(v) => setContactIds(v ? [v] : [])}
                    >
                      <SelectTrigger className={`mt-1.5 rounded-xl bg-white border border-gray-300 h-10 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200`} data-testid="call-contact-select">
                        <SelectValue placeholder="Select a contact" />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-xl border-gray-200 shadow-xl z-[120]">
                        {contacts.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`call-contact-option-${c.id}`}>
                            {c.full_name || c.email || c.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Selected contact details */}
                  {contactIds[0] && (() => {
                    const sel = contacts.find(c => c.id === contactIds[0]);
                    if (!sel) return null;
                    return (
                      <div className="mt-2 space-y-0.5 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                        {sel.title && <p className="text-gray-600 font-medium">{sel.title}</p>}
                        {sel.email && (
                          <p className="truncate">
                            <span className="text-gray-400 mr-1">Email:</span>{sel.email}
                          </p>
                        )}
                        {(sel.mobile || sel.phone) && (
                          <button
                            type="button"
                            onClick={() => {
                              const num = sel.mobile || sel.phone;
                              navigator.clipboard.writeText(num).catch(() => {});
                              toast.success(`Copied: ${num}`);
                            }}
                            className="flex items-center gap-1 font-mono text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded px-0.5 py-0.5 transition-colors"
                            title="Click to copy"
                            data-testid="call-contact-phone-copy"
                          >
                            <Phone className="w-3 h-3" />{sel.mobile || sel.phone}
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Add new contact */}
                  {selectedAccount && !showAddContact && (
                    <button
                      type="button"
                      onClick={() => setShowAddContact(true)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
                      data-testid="call-contact-add-toggle"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Add new contact to this account
                    </button>
                  )}
                  {showAddContact && (
                    <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50/40 p-3 space-y-2" data-testid="call-contact-add-form">
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={newContactFirst} onChange={(e) => setNewContactFirst(e.target.value)} placeholder="First name" className={inputClass} data-testid="call-new-contact-first" />
                        <Input value={newContactLast} onChange={(e) => setNewContactLast(e.target.value)} placeholder="Last name *" className={inputClass} data-testid="call-new-contact-last" />
                        <Input value={newContactPhone} onChange={(e) => setNewContactPhone(e.target.value)} placeholder="Phone" className={inputClass} data-testid="call-new-contact-phone" />
                        <Input value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} placeholder="Email" className={inputClass} data-testid="call-new-contact-email" />
                        <Input value={newContactTitle} onChange={(e) => setNewContactTitle(e.target.value)} placeholder="Job title" className={`col-span-2 ${inputClass}`} data-testid="call-new-contact-title" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowAddContact(false)} className="rounded-xl h-9 px-3 text-xs" data-testid="call-new-contact-cancel">Cancel</Button>
                        <Button type="button" onClick={handleAddContact} disabled={addingContact || !newContactLast.trim()} className="rounded-xl h-9 px-3 text-xs text-white font-semibold" style={{ backgroundColor: '#ee7203' }} data-testid="call-new-contact-save">
                          {addingContact ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Saving…</> : 'Add contact'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>{/* end row 1 */}

              {/* ── Row 2: Call Type + Call Purpose side by side ─────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                {/* Direction (Call Type) */}
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 block">Call Type</Label>
                  <div className="flex rounded-xl overflow-hidden border border-gray-200">
                    <button
                      type="button"
                      data-testid="call-direction-outgoing"
                      onClick={() => setCallDirection('outgoing')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold transition-colors ${callDirection === 'outgoing' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <ArrowUpRight className="w-4 h-4" /> Outgoing
                    </button>
                    <button
                      type="button"
                      data-testid="call-direction-incoming"
                      onClick={() => setCallDirection('incoming')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold transition-colors ${callDirection === 'incoming' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <ArrowDownLeft className="w-4 h-4" /> Incoming
                    </button>
                  </div>
                </div>

                {/* Call Purpose */}
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Call Purpose *</Label>
                  <Select value={purpose} onValueChange={(v) => { setPurpose(v); setPurposeManuallySet(true); }}>
                    <SelectTrigger className={`mt-1.5 ${inputClass}`} data-testid="call-purpose-select">
                      <SelectValue placeholder={purposesLoading ? 'Loading…' : 'Choose a purpose'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-72 z-[120] rounded-xl border-gray-200 shadow-xl">
                      {purposes.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400">No purposes found</div>
                      ) : purposes.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`call-purpose-option-${p.replace(/\s+/g, '-').toLowerCase()}`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>{/* end row 2 */}

              {/* ── Row 3: Business Unit + Linked to Project ─────────────────────── */}
              <div className="grid grid-cols-2 gap-3">
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Business Unit *</Label>
                  <Select value={businessUnit} onValueChange={setBusinessUnit}>
                    <SelectTrigger data-testid="call-business-unit-select" className="mt-1.5 rounded-xl bg-white border border-gray-300 h-10 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                      <SelectValue placeholder="Select business unit" />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl border-gray-200 shadow-xl z-[120]">
                      {BUSINESS_UNITS.map(bu => (
                        <SelectItem key={bu} value={bu}>{bu}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                    Linked to Project{' '}
                    <span className="text-gray-400 font-normal normal-case tracking-normal">(optional)</span>
                  </Label>
                  {linkedPackagesLoading ? (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 h-10">
                      <svg className="w-3.5 h-3.5 animate-spin text-orange-400" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/>
                        <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      <span>Loading packages…</span>
                    </div>
                  ) : (
                    <Select
                      value={linkedPackageId}
                      onValueChange={setLinkedPackageId}
                      disabled={!selectedAccount}
                      data-testid="call-linked-package-select"
                    >
                      <SelectTrigger className="mt-1.5 rounded-xl bg-white border border-gray-300 h-10 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200 disabled:opacity-50">
                        <SelectValue placeholder={selectedAccount ? (linkedPackages.length ? 'Select package…' : 'No packages found') : 'Select account first'} />
                      </SelectTrigger>
                      {linkedPackages.length > 0 && (
                        <SelectContent className="bg-white rounded-xl border-gray-200 shadow-xl z-[120]">
                          <SelectItem value="__none__">None</SelectItem>
                          {linkedPackages.map(pkg => (
                            <SelectItem key={pkg.id} value={`${pkg.module}:${pkg.id}`} data-testid={`call-pkg-option-${pkg.id}`}>
                              {[pkg.project_name, pkg.package_type || pkg.name].filter(Boolean).join(' — ')}
                              {pkg.status ? ` (${pkg.status})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      )}
                    </Select>
                  )}
                </div>
              </div>

              {/* ── Row 4: Notes (large) ─────────────────────────────────────────── */}
              <div className={fieldCardClass}>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Notes</Label>
                <textarea
                  className="w-full rounded-xl border-2 border-gray-300 bg-white focus:border-orange-400 px-3 py-2.5 text-sm resize-y min-h-[140px] focus:outline-none focus:ring-2 focus:ring-orange-200 mt-1.5"
                  placeholder="What was discussed on the call?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="call-notes-input"
                />

                {/* Previous call notes — collapsible, lazy-loaded from Zoho */}
                {selectedAccount && (
                  <div className="mt-3 border-t border-dashed border-gray-200 pt-3">
                    <button
                      type="button"
                      onClick={toggleHistory}
                      className="w-full flex items-center gap-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-orange-600 transition-colors"
                      data-testid="call-history-toggle"
                      aria-expanded={historyOpen}
                    >
                      <History className="w-3.5 h-3.5" />
                      <span>Previous call notes</span>
                      {historyFetchedFor === selectedAccount.id && historyCalls.length > 0 && (
                        <span className="ml-1 text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 font-bold normal-case tracking-normal">
                          {historyCalls.length}
                        </span>
                      )}
                      <span className="ml-auto">
                        {historyOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </span>
                    </button>
                    {historyOpen && (
                      <div className="mt-2 space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="call-history-panel">
                        {historyLoading ? (
                          <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading previous calls…
                          </div>
                        ) : historyCalls.length === 0 ? (
                          <div className="text-xs text-gray-500 py-3 italic">No previous calls logged for this customer in Zoho.</div>
                        ) : (
                          historyCalls.map((c) => (
                            <div key={c.id} className="rounded-xl border border-gray-200 bg-gray-50/70 p-3 text-xs" data-testid={`call-history-item-${c.id}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="font-semibold text-gray-900 truncate">{c.subject || c.call_purpose || 'Call'}</div>
                                  <div className="text-[11px] text-gray-500 mt-0.5">
                                    {[_formatZohoDate(c.call_start_time), c.call_purpose, c.owner_name].filter(Boolean).join(' · ')}
                                  </div>
                                </div>
                              </div>
                              {c.description && (
                                <div className="mt-2 text-gray-700 whitespace-pre-wrap leading-relaxed">{c.description}</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Footer ───────────────────────────────────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2 pt-2 sticky bottom-0 bg-gradient-to-t from-white via-white to-white/60 -mx-4 px-4 py-3 border-t border-orange-100 mt-2">
                {(isCompletingScheduled || isEditing) && (
                  <Button
                    variant="outline"
                    onClick={handleDeleteScheduled}
                    disabled={deleting || submitting}
                    className="rounded-lg h-10 px-4 border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                    data-testid="call-delete-scheduled-btn"
                    title="Delete this call"
                  >
                    {deleting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Deleting…</> : <><Trash2 className="w-4 h-4 mr-2" />Delete</>}
                  </Button>
                )}
                <div className="flex flex-wrap justify-end gap-2 ml-auto">
                  <Button variant="outline" onClick={() => { if (modalUiV2) activityBar.discardActive(); onClose?.(); }} className="rounded-lg h-10 px-4 border-2" data-testid="call-cancel-btn">Cancel</Button>
                  <Button
                    onClick={() => handleSubmit('close')}
                    disabled={submitting || !selectedAccount || !purpose || !businessUnit}
                    className="rounded-lg h-10 px-5 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                    style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                    data-testid="call-submit-btn"
                  >
                    {submitting && submittingMode === 'close' ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />{isEditing ? 'Saving…' : isCompletingScheduled ? 'Completing…' : 'Logging…'}</>
                    ) : (
                      <><Phone className="w-4 h-4 mr-2" />{isEditing ? 'Save' : isCompletingScheduled ? 'Mark Completed' : 'Add to CRM'}</>
                    )}
                  </Button>
                  {!isCompletingScheduled && (
                    <Button
                      onClick={() => handleSubmit('view')}
                      disabled={submitting || !selectedAccount || !purpose || !businessUnit}
                      className="rounded-lg h-10 px-5 text-white font-semibold shadow-md hover:shadow-lg transition-all bg-gray-900 hover:bg-black"
                      data-testid="call-submit-view-btn"
                    >
                      {submitting && submittingMode === 'view' ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" />{isEditing ? 'Saving…' : 'Logging…'}</>
                      ) : (
                        <><ExternalLink className="w-4 h-4 mr-2" />{isEditing ? 'Save & View on CRM' : 'Add & View on CRM'}</>
                      )}
                    </Button>
                  )}
                  {!isCompletingScheduled && !isEditing && onChainToQuote && (
                    <Button
                      onClick={() => handleSubmit('chain-quote')}
                      disabled={submitting || !selectedAccount || !purpose || !businessUnit}
                      className="rounded-lg h-10 px-5 text-white font-semibold shadow-md hover:shadow-lg transition-all bg-blue-600 hover:bg-blue-700"
                      data-testid="call-submit-then-quote-btn"
                      title="Log this call and immediately open the Add Quote form pre-filled with the same account"
                    >
                      {submitting && submittingMode === 'chain-quote' ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" />Logging…</>
                      ) : (
                        <><FileText className="w-4 h-4 mr-2" />Add & Then Quote</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddCallToCrmModal;
